import type {
  GetSeoMetaParams,
  GetStructuredDataParams,
  PortfolioItem,
  SeoMeta,
  StructuredData,
} from '../store/types';

const SITE_URL = 'https://seroujkhandjihanian.com/';
const DEFAULT_SOCIAL_IMAGE = 'https://seroujkhandjihanian.com/assets/images/seroujkhandjihanian.webp';
const SERVICES_SCHEMA_ITEMS = [
  {
    title: 'Web Development',
    description: 'Custom websites, e-commerce platforms, blogs, and scalable web solutions with hosting and ongoing maintenance.',
  },
  {
    title: 'App Development',
    description: 'Cross-platform mobile app development for iOS and Android, including deployment, updates, and maintenance.',
  },
  {
    title: 'UI | UX',
    description: 'Social media management and intuitive UI/UX design for web and mobile',
  },
];

const toAbsoluteUrl = (path: string): string => {
  if (path === '/') return SITE_URL;
  return `${SITE_URL}${path.replace(/^\//, '')}`;
};

const getProjectSeoMeta = (project: PortfolioItem): SeoMeta => {
  const description = [
    project.info,
    `Category: ${project.category.join(', ')}`,
    `Technologies: ${project.technologies.join(', ')}`,
  ].join(' | ').slice(0, 155);
  const keywords = [
    project.title,
    ...project.category,
    ...project.technologies,
    'portfolio project',
    'Serouj Khanjihanian',
    'software developer',
    'web development',
    'app development',
  ].join(', ');

  return {
    title: `${project.title} | Portfolio Project | Serouj Khanjihanian`,
    description,
    canonical: toAbsoluteUrl(`/my-portfolio/${project.key}`),
    image: project.images[0]
      ? `${SITE_URL}dataImages/${project.images[0]}`
      : DEFAULT_SOCIAL_IMAGE,
    keywords,
  };
};

export const getSeoMeta = ({
  activeSidebar,
  showPortfolioPopup,
  activeProject,
}: GetSeoMetaParams): SeoMeta => {
  if (showPortfolioPopup && activeProject) {
    return getProjectSeoMeta(activeProject);
  }

  switch (activeSidebar) {
    case 'about':
      return {
        title: 'About Serouj Khanjihanian | Software Developer',
        description:
          'Learn about Serouj Khanjihanian, a Lebanese software developer with experience in mobile and full-stack web development, testing, and product delivery.',
        canonical: toAbsoluteUrl('/about'),
        image: DEFAULT_SOCIAL_IMAGE,
        keywords:
          'about Serouj Khanjihanian, software developer bio, Lebanese software engineer, React Native developer, full stack developer, web developer, mobile developer',
      };
    case 'services':
      return {
        title: 'Software Development Services | Serouj Khanjihanian',
        description:
          'Explore web development, mobile app development, UI/UX, and software engineering skills offered by Serouj Khanjihanian.',
        canonical: toAbsoluteUrl('/services'),
        image: DEFAULT_SOCIAL_IMAGE,
        keywords:
          'software development services, web development services, mobile app development services, UI UX design, React Native services, Laravel development, WordPress development',
      };
    case 'portfolio':
      return {
        title: 'Software Development Portfolio | Serouj Khanjihanian',
        description:
          'Explore software projects built by Serouj Khanjihanian across web, mobile, and full-stack development.',
        canonical: toAbsoluteUrl('/my-portfolio'),
        image: DEFAULT_SOCIAL_IMAGE,
        keywords:
          'software portfolio, web development portfolio, mobile app portfolio, full stack portfolio, React Native portfolio, Laravel portfolio, Serouj Khanjihanian projects',
      };
    case 'connect':
      return {
        title: 'Connect With Serouj Khanjihanian | Software Engineer',
        description:
          'Get in touch with Serouj Khanjihanian for software engineering, mobile, and web development collaborations.',
        canonical: toAbsoluteUrl('/connect'),
        image: DEFAULT_SOCIAL_IMAGE,
        keywords:
          'contact Serouj Khanjihanian, hire software developer, web developer contact, mobile app developer contact, freelance developer Lebanon',
      };
    default:
      return {
        title: 'Serouj Khanjihanian | Software Engineer',
        description:
          'Serouj Khanjihanian, a software developer specializing in building scalable applications and solving complex programming challenges.',
        canonical: SITE_URL,
        image: DEFAULT_SOCIAL_IMAGE,
        keywords:
          'Serouj Khanjihanian, software engineer, full stack developer, web developer, mobile developer, React Native, Laravel, portfolio',
      };
  }
};

export const getStructuredData = ({
  activeSidebar,
  showPortfolioPopup,
  activeProject,
  portfolioItems,
  seoMeta,
}: GetStructuredDataParams): StructuredData | null => {
  if (showPortfolioPopup && activeProject) {
    return {
      '@context': 'https://schema.org',
      '@type': 'CreativeWork',
      name: activeProject.title,
      description: activeProject.info,
      datePublished: activeProject.date,
      genre: activeProject.category,
      keywords: activeProject.technologies,
      url: seoMeta.canonical,
      image: seoMeta.image,
      author: {
        '@type': 'Person',
        name: 'Serouj Khanjihanian',
        url: SITE_URL,
      },
    };
  }

  switch (activeSidebar) {
    case 'about':
      return {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: 'Serouj Khanjihanian',
        jobTitle: 'Software Developer',
        url: SITE_URL,
        image: DEFAULT_SOCIAL_IMAGE,
        email: 'mailto:seroujkh@hotmail.com',
        telephone: '+96171032898',
        nationality: 'Lebanese',
        knowsLanguage: ['English', 'Arabic', 'Armenian'],
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'Lebanon',
        },
        sameAs: [
          'https://www.linkedin.com/in/serouj-khandjihanian-895997183/',
          'https://www.instagram.com/seroujkhandjihanian/',
          'https://x.com/seroujkh',
        ],
      };
    case 'services':
      return {
        '@context': 'https://schema.org',
        '@type': 'ProfessionalService',
        name: 'Serouj Khanjihanian - Software Development Services',
        url: SITE_URL,
        description: seoMeta.description,
        areaServed: 'Worldwide',
        provider: {
          '@type': 'Person',
          name: 'Serouj Khanjihanian',
        },
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Software Development Services',
          itemListElement: SERVICES_SCHEMA_ITEMS.map((item) => ({
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: item.title,
              description: item.description,
            },
          })),
        },
      };
    case 'connect':
      return {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: 'Connect With Serouj Khanjihanian',
        url: seoMeta.canonical,
        description: seoMeta.description,
        mainEntity: {
          '@type': 'Person',
          name: 'Serouj Khanjihanian',
          email: 'mailto:seroujkh@hotmail.com',
          telephone: '+96171032898',
          sameAs: [
            'https://www.linkedin.com/in/serouj-khandjihanian-895997183/',
            'https://www.instagram.com/seroujkhandjihanian/',
            'https://x.com/seroujkh',
          ],
        },
      };
    case 'portfolio':
      return {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Software Development Portfolio',
        url: seoMeta.canonical,
        description: seoMeta.description,
        mainEntity: {
          '@type': 'ItemList',
          itemListElement: portfolioItems.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            url: toAbsoluteUrl(`/my-portfolio/${item.key}`),
            name: item.title,
          })),
        },
      };
    default:
      return {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'WebSite',
            name: 'Serouj Khanjihanian',
            url: SITE_URL,
            description: seoMeta.description,
            inLanguage: 'en',
          },
          {
            '@type': 'Person',
            name: 'Serouj Khanjihanian',
            jobTitle: 'Software Engineer',
            url: SITE_URL,
            image: DEFAULT_SOCIAL_IMAGE,
            email: 'mailto:seroujkh@hotmail.com',
            sameAs: [
              'https://www.linkedin.com/in/serouj-khandjihanian-895997183/',
              'https://www.instagram.com/seroujkhandjihanian/',
              'https://x.com/seroujkh',
            ],
          },
        ],
      };
  }
};
